// src/utils/dealTeamFields.js
import { withLoadedModifiedDate } from '@1823-partners/core';

/**
 * Column-metadata helpers shared by the deal-team grid and the deal form.
 *
 * The deal-team workspace is metadata-driven: the backend reports the columns
 * of the selected CRE source and which of them the append-only contract
 * manages. The grid and the full-deal modal have to agree on those rules, so
 * they live here rather than in either component.
 */

// Identity and audit columns the workspace never lets an editor type over.
export const READ_ONLY_COLUMNS = new Set([
  'DEAL_ID',
  'DEALID',
  'DEAL_NUMBER',
  'MODIFIED_DATE',
  'MODIFIED_BY',
]);

// Intake provenance: hidden in the grid by default, and shown read-only in the
// deal form so an editor can see where a deal came from without retyping ids
// the intake pipeline owns.
export const DEFAULT_HIDDEN_COLUMNS = new Set([
  'DEAL_ID',
  'CONVERSATION_ID',
  'SOURCE_MSG_ID',
]);

// The writer stamps both on every write; MODIFIED_DATE is re-attached from the
// loaded baseline row via withLoadedModifiedDate so an edited row's own copy
// is never trusted.
export const SERVER_STAMPED_COLUMNS = new Set(['MODIFIED_DATE', 'MODIFIED_BY']);

// Starter vocabularies for the intake grid. The table is young, so inferring
// choices from loaded rows alone yields a near-empty dropdown; these seed it
// with reasonable options. Values observed in the data are still offered, so
// nothing an editor or the intake pipeline already wrote becomes unselectable.
// STAGE is uppercase to match the CRE.DEALS column default of 'NEW'.
export const DEAL_DROPDOWN_FIELDS = {
  STAGE: ['NEW', 'SCREENING', 'UNDERWRITING', 'TERM SHEET', 'CLOSED', 'PASSED'],
  PROPERTY_TYPE: [
    'Multifamily',
    'Office',
    'Retail',
    'Industrial',
    'Hospitality',
    'Self-Storage',
    'Mixed-Use',
    'Other',
  ],
};

// Free-text columns that hold prose rather than a value; the form gives them a
// full-width multiline input instead of a one-line field.
const LONG_TEXT_COLUMN_RE = /DESCRIPTION|NOTES|COMMENT|SUMMARY|BODY|TEXT/;
const LONG_TEXT_VALUE_LENGTH = 80;

export const isNumericType = (type = '') =>
  /NUMBER|DECIMAL|NUMERIC|INT|FLOAT|DOUBLE|REAL/.test(type.toUpperCase());

export const isDateTimeType = (type = '') => /TIMESTAMP|DATETIME/.test(type.toUpperCase());
export const isDateType = (type = '') => type.toUpperCase() === 'DATE';
export const isBooleanType = (type = '') => type.toUpperCase() === 'BOOLEAN';
export const isStructuredType = (type = '') => /VARIANT|OBJECT|ARRAY/.test(type.toUpperCase());

/**
 * Whether an editor may change *column* on the current source.
 *
 * Structured and timestamp columns stay read-only everywhere: a VARIANT round
 * trip through a text editor corrupts its type, and the timestamps are either
 * intake facts or the recency stamp the backend owns.
 *
 * The row-status column is read-only too. It is the bitemporal state of the
 * record rather than a fact about the deal, so retiring a deal is the grid's
 * delete action; leaving it in the editable set would let an ordinary edit
 * disappear a deal as a side effect. It is matched through the backend's
 * declared *status_column*, so a source without that contract keeps a column
 * merely named STATUS editable.
 */
export const isColumnEditable = (column, metadata, canWrite) => Boolean(
  metadata?.writable &&
  canWrite &&
  column?.name !== metadata.key_column &&
  column?.name !== metadata.recency_column &&
  column?.name !== metadata.status_column &&
  !READ_ONLY_COLUMNS.has(column?.name) &&
  !isStructuredType(column?.data_type) &&
  !isDateTimeType(column?.data_type)
);

// Columns that carry a name worth showing a human, in preference order; the
// deal key is the fallback.
const TITLE_COLUMNS = ['DEAL_NAME', 'NAME', 'PROPERTY_NAME'];

/** The friendliest name *row* has, for a dialog title or a confirmation. */
export const dealLabel = (row, metadata) => {
  const named = TITLE_COLUMNS.map((name) => row?.[name]).find(Boolean);
  const key = metadata?.key_column ? row?.[metadata.key_column] : undefined;
  return String(named ?? key ?? '');
};

/** Column names carrying a semi-structured payload on *metadata*. */
export const structuredColumns = (metadata) => (metadata?.columns || [])
  .filter((column) => isStructuredType(column.data_type))
  .map((column) => column.name);

/**
 * Drop the columns the backend stamps, keeping the rest of the current state
 * and attaching the loaded row's MODIFIED_DATE concurrency token unchanged.
 *
 * The save endpoint requires a complete row, so this filters rather than picks.
 */
export const toAppendPayload = (row, metadata, loadedRow = row) => {
  if (!row) return {};
  const filtered = Object.fromEntries(
    Object.entries(row).filter(([column]) =>
      column !== metadata?.recency_column && !SERVER_STAMPED_COLUMNS.has(column))
  );
  return withLoadedModifiedDate(filtered, loadedRow || row);
};

// A save-path round trip can store a payload as a JSON *string*, sometimes
// more than one layer deep; bound the peeling so a pathological value cannot
// spin.
const MAX_STRING_ENCODING_DEPTH = 5;

/**
 * Peel the JSON string layers a save-path round trip can wrap a payload in.
 *
 * Returns the innermost text and whether any layer was removed — a VARIANT
 * holding its payload as text is a defect worth surfacing, not an equivalent
 * value.
 */
export const unwrapStringEncodedJson = (value) => {
  let text = String(value);
  let wasStringEncoded = false;
  for (let depth = 0; depth < MAX_STRING_ENCODING_DEPTH && /^\s*"/.test(text); depth += 1) {
    let unwrapped;
    try {
      unwrapped = JSON.parse(text);
    } catch (error) {
      break;
    }
    if (typeof unwrapped !== 'string') break;
    text = unwrapped;
    wasStringEncoded = true;
  }
  return { text, wasStringEncoded };
};

/**
 * Render a VARIANT/OBJECT/ARRAY value for display.
 *
 * Stored text passes through character-for-character: Snowflake already
 * returns a VARIANT as indented JSON, and re-stringifying it would push every
 * numeric lexeme through a double, so an id past 2^53 would render rounded. An
 * object arrived parsed, so its numbers are doubles already and indenting it
 * loses nothing.
 */
export const formatStructuredValue = (value) => {
  if (value === null || value === undefined) return '';
  if (typeof value !== 'string') return JSON.stringify(value, null, 2);
  return unwrapStringEncodedJson(value).text;
};

/**
 * Read a numeric input back as a number.
 *
 * A cleared field is a NULL, not a zero. Text that is not a number cannot come
 * from a number input, so it falls back to *fallback* rather than silently
 * becoming NULL if it ever arrives from a paste or a programmatic edit.
 */
export const parseNumericInput = (raw, fallback = null) => {
  const text = String(raw ?? '').trim();
  if (text === '') return null;
  const parsed = Number(text);
  return Number.isFinite(parsed) ? parsed : fallback;
};

export const isLongTextColumn = (name, value) =>
  LONG_TEXT_COLUMN_RE.test(String(name || '').toUpperCase()) ||
  String(value ?? '').length > LONG_TEXT_VALUE_LENGTH;

// Values are compared case- and whitespace-insensitively so an observed 'New'
// collapses onto the seeded 'NEW' instead of offering the editor two spellings
// of one stage.
const choiceKey = (value) => String(value).trim().toUpperCase();

export const buildDealDropdownValues = (rows, source) => {
  if (source?.name !== 'DEALS') return {};

  return Object.fromEntries(
    Object.entries(DEAL_DROPDOWN_FIELDS).map(([field, defaults]) => {
      const seen = new Set(defaults.map(choiceKey));
      const extras = [];
      (rows || []).forEach((row) => {
        const value = row[field];
        if (value === null || value === undefined || String(value).trim() === '') return;
        const key = choiceKey(value);
        if (seen.has(key)) return;
        seen.add(key);
        extras.push(value);
      });
      extras.sort((left, right) => String(left).localeCompare(String(right)));
      return [field, ['', ...defaults, ...extras]];
    })
  );
};
